import {
	keepPreviousData,
	type QueryClient,
	useMutation,
	useQueries,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import {
	apiAdapter,
	type CheckProviderKeyInput,
	type ConversationFilters,
	type CreateAgentInput,
	type CreateApiKeyInput,
	type CreateReplayInput,
	type CreateTriggerInput,
	type InviteMemberInput,
	type McpProbeInput,
	type MintBadgeInput,
	type ModelCatalogFilters,
	type SandboxClassInput,
	type UpdateAgentInput,
	type UpdateApiKeyInput,
	type UpdateBadgeInput,
	type UpdateSandboxClassInput,
	type UpdateTriggerInput,
	type UpdateWorkspaceInput,
	type UpsertEgressConnectionInput,
	type UpsertMcpServerInput,
	type UpsertProviderInput,
	type UsageQuery,
} from "./adapter";
import type {
	AgentListItem,
	ApprovalDecisionInput,
	ApprovalFilters,
	ConnectSlackBotInput,
	SlackBotStatus,
	WorkspaceMember,
} from "./types";

export const queryKeys = {
	version: ["version"] as const,
	agents: ["agents"] as const,
	agentDetail: (slug: string) => ["agents", slug] as const,
	agentFiles: (slug: string) => ["agent-files", slug] as const,
	agentFileContent: (slug: string, path: string) => ["agent-file-content", slug, path] as const,
	agentDeployments: (slug: string) => ["agent-deployments", slug] as const,
	agentTriggers: (slug: string) => ["agent-triggers", slug] as const,
	agentRuns: (slug: string, triggerId?: string) =>
		["agent-runs", slug, triggerId ?? "all"] as const,
	workspaceTriggers: ["workspace-triggers"] as const,
	triggerBadge: (slug: string, triggerId: string) => ["trigger-badge", slug, triggerId] as const,
	slackBot: (slug: string) => ["slack-bot", slug] as const,
	agentReplays: (slug: string) => ["agent-replays", slug] as const,
	workerStatus: (slug: string) => ["worker-status", slug] as const,
	sandboxClasses: ["sandbox-classes"] as const,
	sandboxClass: (id: string) => ["sandbox-classes", id] as const,
	conversations: (filters: ConversationFilters) => ["conversations", filters] as const,
	conversationDetail: (id: string) => ["conversations", id] as const,
	egressConnections: (slug: string) => ["egress-connections", slug] as const,
	egressLog: (slug: string) => ["egress-log", slug] as const,
	egressBoundary: ["egress-boundary"] as const,
	egressPresets: ["egress-presets"] as const,
	mcpServers: (slug: string) => ["mcp-servers", slug] as const,
	mcpPresets: ["mcp-presets"] as const,
	customProviders: ["custom-providers"] as const,
	models: (filters: ModelCatalogFilters) => ["models", filters] as const,
	apiKeys: ["api-keys"] as const,
	usageOverview: (query: UsageQuery) => ["usage-overview", query] as const,
	usageByAgent: (slug: string, query: UsageQuery) => ["usage-agent", slug, query] as const,
	usageByApiKey: (id: string, query: UsageQuery) => ["usage-api-key", id, query] as const,
	workspaceSettings: ["workspace-settings"] as const,
	workspaceMembers: ["workspace-members"] as const,
	approvals: (filters: ApprovalFilters) => ["approvals", filters] as const,
	approval: (id: string) => ["approvals", "detail", id] as const,
	approvalPendingCount: ["approvals", "pending-count"] as const,
	workspaceInvitations: ["workspace-invitations"] as const,
};

export function useVersionQuery() {
	return useQuery({
		queryKey: queryKeys.version,
		queryFn: () => apiAdapter.version(),
		staleTime: 5 * 60 * 1000,
	});
}

export function useAgentsQuery() {
	return useQuery({
		queryKey: queryKeys.agents,
		queryFn: () => apiAdapter.agents.list(),
	});
}

/** Warm the agent registry cache outside the React tree (e.g. during boot).
 * Never throws: on failure the page's own query fetches and retries. */
export function prefetchAgents(queryClient: QueryClient) {
	return queryClient.prefetchQuery({
		queryKey: queryKeys.agents,
		queryFn: () => apiAdapter.agents.list(),
	});
}

export function useModelCatalogQuery(filters: ModelCatalogFilters = {}) {
	return useQuery({
		queryKey: queryKeys.models(filters),
		queryFn: () => apiAdapter.models.list(filters),
	});
}

export function useAgentDetailQuery(slug: string) {
	return useQuery({
		queryKey: queryKeys.agentDetail(slug),
		queryFn: () => apiAdapter.agents.get(slug),
		enabled: Boolean(slug),
	});
}

/** Live supervisor state for a worker agent; polls while the card is mounted. */
export function useWorkerStatusQuery(slug: string) {
	return useQuery({
		queryKey: queryKeys.workerStatus(slug),
		queryFn: () => apiAdapter.agents.workerStatus(slug),
		enabled: Boolean(slug),
		refetchInterval: 5000,
	});
}

export function useResetWorkerMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => apiAdapter.agents.resetWorker(slug),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.workerStatus(slug) });
		},
	});
}

export function useCreateAgentMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (input: CreateAgentInput) => apiAdapter.agents.create(input),
		onSuccess: (created) => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.agents });
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentDetail(created.slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.sandboxClasses });
		},
	});
}

export function useUpdateAgentMutation(slug: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (input: UpdateAgentInput) => apiAdapter.agents.update(slug, input),
		onSuccess: (updated) => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.agents });
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentDetail(slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentDetail(updated.slug) });
			// Config saves record a deployment snapshot server-side.
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentDeployments(slug) });
			// The class registry counts agents per class.
			void queryClient.invalidateQueries({ queryKey: queryKeys.sandboxClasses });
		},
	});
}

export function useDeleteAgentMutation(slug: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => apiAdapter.agents.delete(slug),
		onSuccess: () => {
			queryClient.removeQueries({ queryKey: queryKeys.agentDetail(slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.agents });
			void queryClient.invalidateQueries({ queryKey: queryKeys.sandboxClasses });
		},
	});
}

export function useAgentFilesQuery(slug: string) {
	return useQuery({
		queryKey: queryKeys.agentFiles(slug),
		queryFn: () => apiAdapter.agentFiles.list(slug),
		enabled: Boolean(slug),
	});
}

export function useAgentFileContentQuery(slug: string, path: string | null) {
	return useQuery({
		queryKey: queryKeys.agentFileContent(slug, path ?? ""),
		queryFn: () => apiAdapter.agentFiles.getContent(slug, path as string),
		enabled: Boolean(slug) && Boolean(path),
	});
}

export function useUploadAgentFilesMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (files: Array<{ path: string; content: string }>) =>
			apiAdapter.agentFiles.upload(slug, files),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentFiles(slug) });
		},
	});
}

export function useDeleteAgentFileMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (path: string) => apiAdapter.agentFiles.delete(slug, path),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentFiles(slug) });
		},
	});
}

export function useSandboxClassesQuery() {
	return useQuery({
		queryKey: queryKeys.sandboxClasses,
		queryFn: () => apiAdapter.sandboxClasses.list(),
	});
}

export function useSandboxClassQuery(id: string) {
	return useQuery({
		queryKey: queryKeys.sandboxClass(id),
		queryFn: () => apiAdapter.sandboxClasses.get(id),
		enabled: Boolean(id),
	});
}

function invalidateSandboxClasses(queryClient: QueryClient, id?: string) {
	void queryClient.invalidateQueries({ queryKey: queryKeys.sandboxClasses });
	if (id) void queryClient.invalidateQueries({ queryKey: queryKeys.sandboxClass(id) });
}

export function useCreateSandboxClassMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: SandboxClassInput) => apiAdapter.sandboxClasses.create(input),
		onSuccess: (created) => invalidateSandboxClasses(queryClient, created.id),
	});
}

export function useUpdateSandboxClassMutation(id: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: UpdateSandboxClassInput) => apiAdapter.sandboxClasses.update(id, input),
		onSuccess: () => invalidateSandboxClasses(queryClient, id),
	});
}

export function useDeleteSandboxClassMutation(id: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => apiAdapter.sandboxClasses.remove(id),
		onSuccess: () => {
			queryClient.removeQueries({ queryKey: queryKeys.sandboxClass(id) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.sandboxClasses });
			// Agents on the class fell back to host defaults server-side.
			void queryClient.invalidateQueries({ queryKey: queryKeys.agents });
		},
	});
}

export function useAgentDeploymentsQuery(slug: string) {
	return useQuery({
		queryKey: queryKeys.agentDeployments(slug),
		queryFn: () => apiAdapter.deployments.list(slug),
		enabled: Boolean(slug),
	});
}

export function usePromoteDeploymentMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (deploymentId: string) => apiAdapter.deployments.promote(slug, deploymentId),
		onSuccess: () => {
			// Promote rewrites config and files: refresh everything about the agent.
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentDeployments(slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentDetail(slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentFiles(slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.agents });
		},
	});
}

export function useAgentTriggersQuery(slug: string) {
	return useQuery({
		queryKey: queryKeys.agentTriggers(slug),
		queryFn: () => apiAdapter.triggers.list(slug),
		enabled: Boolean(slug),
	});
}

export function useWorkspaceTriggersQuery() {
	return useQuery({
		queryKey: queryKeys.workspaceTriggers,
		queryFn: () => apiAdapter.triggers.listAll(),
	});
}

export function useAgentRunsQuery(slug: string, triggerId?: string) {
	return useQuery({
		queryKey: queryKeys.agentRuns(slug, triggerId),
		queryFn: () => apiAdapter.triggers.runs(slug, { triggerId }),
		enabled: Boolean(slug),
		// Unattended runs land while the page is open: keep the ledger fresh.
		refetchInterval: 15_000,
	});
}

export function useAgentReplaysQuery(slug: string) {
	return useQuery({
		queryKey: queryKeys.agentReplays(slug),
		queryFn: () => apiAdapter.replays.list(slug),
		enabled: slug.length > 0,
		// Keep polling while any replay is still running so statuses settle live.
		refetchInterval: (query) =>
			query.state.data?.some((replay) => replay.status === "running") ? 4000 : false,
	});
}

export function useCreateReplaysMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateReplayInput) => apiAdapter.replays.create(slug, input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentReplays(slug) });
		},
	});
}

export function useCreateTriggerMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateTriggerInput) => apiAdapter.triggers.create(slug, input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentTriggers(slug) });
		},
	});
}

export function useUpdateTriggerMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, input }: { id: string; input: UpdateTriggerInput }) =>
			apiAdapter.triggers.update(slug, id, input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentTriggers(slug) });
		},
	});
}

export function useDeleteTriggerMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => apiAdapter.triggers.delete(slug, id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentTriggers(slug) });
		},
	});
}

export function useTriggerBadgeQuery(slug: string, triggerId: string) {
	return useQuery({
		queryKey: queryKeys.triggerBadge(slug, triggerId),
		queryFn: () => apiAdapter.triggers.badge(slug, triggerId),
		enabled: Boolean(slug) && Boolean(triggerId),
	});
}

export function useMintBadgeMutation(slug: string, triggerId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: MintBadgeInput) => apiAdapter.triggers.mintBadge(slug, triggerId, input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.triggerBadge(slug, triggerId) });
		},
	});
}

export function useUpdateBadgeMutation(slug: string, triggerId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: UpdateBadgeInput) =>
			apiAdapter.triggers.updateBadge(slug, triggerId, input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.triggerBadge(slug, triggerId) });
		},
	});
}

export function useRevokeBadgeMutation(slug: string, triggerId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => apiAdapter.triggers.revokeBadge(slug, triggerId),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.triggerBadge(slug, triggerId) });
		},
	});
}

export function useSlackBotQuery(slug: string) {
	return useQuery({
		queryKey: queryKeys.slackBot(slug),
		queryFn: () => apiAdapter.slackBot.get(slug),
		enabled: Boolean(slug),
	});
}

/** Every write answers with the new status, so the card updates without a refetch. */
function useSlackBotWrite<TInput>(slug: string, write: (input: TInput) => Promise<SlackBotStatus>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: write,
		onSuccess: (status) => {
			queryClient.setQueryData(queryKeys.slackBot(slug), status);
		},
	});
}

export function useConnectSlackBotMutation(slug: string) {
	return useSlackBotWrite(slug, (input: ConnectSlackBotInput) =>
		apiAdapter.slackBot.connect(slug, input),
	);
}

export function useSetSlackBotEnabledMutation(slug: string) {
	return useSlackBotWrite(slug, (enabled: boolean) =>
		apiAdapter.slackBot.setEnabled(slug, enabled),
	);
}

export function useDisconnectSlackBotMutation(slug: string) {
	return useSlackBotWrite<void>(slug, async () => {
		await apiAdapter.slackBot.disconnect(slug);
		return { connected: false } as const;
	});
}

export function useConversationsQuery(filters: ConversationFilters) {
	return useQuery({
		queryKey: queryKeys.conversations(filters),
		queryFn: () => apiAdapter.conversations.list(filters),
		// Titles are generated asynchronously shortly after a turn completes,
		// so always refetch on mount instead of serving a recent cache.
		staleTime: 0,
	});
}

export function useConversationDetailQuery(id: string) {
	return useQuery({
		queryKey: queryKeys.conversationDetail(id),
		queryFn: () => apiAdapter.conversations.get(id),
		enabled: Boolean(id),
	});
}

export function useEgressConnectionsQuery(slug: string) {
	return useQuery({
		queryKey: queryKeys.egressConnections(slug),
		queryFn: () => apiAdapter.egress.list(slug),
		enabled: Boolean(slug),
	});
}

export function useEgressLogQuery(slug: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.egressLog(slug),
		queryFn: () => apiAdapter.egress.log(slug),
		enabled: Boolean(slug) && enabled,
		refetchInterval: 10_000,
	});
}

export function useEgressBoundaryStatusQuery() {
	return useQuery({
		queryKey: queryKeys.egressBoundary,
		queryFn: () => apiAdapter.egress.boundaryStatus(),
		refetchInterval: 15_000,
	});
}

export function useEgressPresetsQuery() {
	return useQuery({
		queryKey: queryKeys.egressPresets,
		queryFn: () => apiAdapter.egress.presets(),
		staleTime: Number.POSITIVE_INFINITY,
	});
}

export function useProvidersQuery() {
	return useQuery({
		queryKey: queryKeys.customProviders,
		queryFn: () => apiAdapter.providers.list(),
	});
}

function invalidateProviderDependents(queryClient: QueryClient) {
	void queryClient.invalidateQueries({ queryKey: queryKeys.customProviders });
	// Provider instances feed the model catalog and the egress preset chips.
	void queryClient.invalidateQueries({ queryKey: ["models"] });
	void queryClient.invalidateQueries({ queryKey: queryKeys.egressPresets });
}

export function useUpsertProviderMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: UpsertProviderInput) => apiAdapter.providers.upsert(input),
		onSuccess: () => invalidateProviderDependents(queryClient),
	});
}

export function useCheckProviderKeyMutation() {
	return useMutation({
		mutationFn: (input: CheckProviderKeyInput) => apiAdapter.providers.check(input),
	});
}

export function useDeleteProviderMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (name: string) => apiAdapter.providers.delete(name),
		onSuccess: () => invalidateProviderDependents(queryClient),
	});
}

/** Environment variables live on the agent and snapshot a deployment when they change. */
function invalidateAgentEnv(queryClient: QueryClient, slug: string) {
	void queryClient.invalidateQueries({ queryKey: queryKeys.agentDetail(slug) });
	void queryClient.invalidateQueries({ queryKey: queryKeys.agentDeployments(slug) });
}

export function useSetAgentSecretMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ key, value }: { key: string; value: string }) =>
			apiAdapter.agents.setSecret(slug, key, value),
		onSuccess: () => invalidateAgentEnv(queryClient, slug),
	});
}

export function useDeleteAgentSecretMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (key: string) => apiAdapter.agents.deleteSecret(slug, key),
		onSuccess: () => invalidateAgentEnv(queryClient, slug),
	});
}

export function useUpsertEgressConnectionMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: UpsertEgressConnectionInput) => apiAdapter.egress.upsert(slug, input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.egressConnections(slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentDetail(slug) });
		},
	});
}

export function useDeleteEgressConnectionMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (name: string) => apiAdapter.egress.delete(slug, name),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.egressConnections(slug) });
		},
	});
}

export function useMcpServersQuery(slug: string) {
	return useQuery({
		queryKey: queryKeys.mcpServers(slug),
		queryFn: () => apiAdapter.mcp.list(slug),
		enabled: Boolean(slug),
	});
}

export function useMcpPresetsQuery() {
	return useQuery({
		queryKey: queryKeys.mcpPresets,
		queryFn: () => apiAdapter.mcp.presets(),
		staleTime: 60 * 60 * 1000,
	});
}

export function useMcpOAuthConnectMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: {
			name: string;
			url?: string;
			clientId?: string;
			clientSecret?: string;
			scopes?: string[];
		}) => {
			const { name, ...connection } = input;
			return apiAdapter.mcp.connectOAuth(slug, name, connection);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.mcpServers(slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentDeployments(slug) });
		},
	});
}

export function useMcpOAuthDisconnectMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ name }: { name: string }) => apiAdapter.mcp.disconnectOAuth(slug, name),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.mcpServers(slug) });
		},
	});
}

export function useMcpProbeMutation(slug: string) {
	return useMutation({
		mutationFn: (input: McpProbeInput) => apiAdapter.mcp.probe(slug, input),
	});
}

export function useUpsertMcpServerMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: UpsertMcpServerInput) => apiAdapter.mcp.upsert(slug, input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.mcpServers(slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.egressConnections(slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentDetail(slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentDeployments(slug) });
		},
	});
}

export function useDeleteMcpServerMutation(slug: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (name: string) => apiAdapter.mcp.delete(slug, name),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.mcpServers(slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.egressConnections(slug) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.agentDeployments(slug) });
		},
	});
}

/** Owners and admins only: pass `enabled: false` for a member so no 403 is fetched. */
export function useApiKeysQuery({ enabled = true }: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: queryKeys.apiKeys,
		queryFn: () => apiAdapter.apiKeys.list(),
		enabled,
	});
}

export function useCreateApiKeyMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (input: CreateApiKeyInput) => apiAdapter.apiKeys.create(input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys });
		},
	});
}

export function useUpdateApiKeyMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, input }: { id: string; input: UpdateApiKeyInput }) =>
			apiAdapter.apiKeys.update(id, input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys });
		},
	});
}

export function useRevokeApiKeyMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => apiAdapter.apiKeys.revoke(id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys });
		},
	});
}

export function useDeleteApiKeyMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => apiAdapter.apiKeys.delete(id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys });
		},
	});
}

export function useUsageOverviewQuery(query: UsageQuery) {
	return useQuery({
		queryKey: queryKeys.usageOverview(query),
		queryFn: () => apiAdapter.usage.overview(query),
		placeholderData: keepPreviousData,
	});
}

export function useUsageByAgentQuery(slug: string, query: UsageQuery) {
	return useQuery({
		queryKey: queryKeys.usageByAgent(slug, query),
		queryFn: () => apiAdapter.usage.byProfile(slug, query),
		enabled: Boolean(slug),
		placeholderData: keepPreviousData,
	});
}

/**
 * Every agent's usage aggregate, one query each.
 *
 * There is no workspace-wide per-agent breakdown to ask for, so the registry
 * fans out over the per-agent endpoint instead. The window opens at the agent's own creation
 * date and has no upper bound, which makes the answer all-time: "has this ever
 * run" is the question the registry is really asking, and a rolling window
 * cannot answer it. React Query keys each one the same way the usage page does,
 * so the two share a cache.
 */
export function useAgentUsageQueries(agents: readonly AgentListItem[] | undefined) {
	return useQueries({
		queries: (agents ?? []).map((agent) => {
			const query: UsageQuery = { from: agent.createdAt, to: "", granularity: "day" };
			return {
				queryKey: queryKeys.usageByAgent(agent.slug, query),
				queryFn: () => apiAdapter.usage.byProfile(agent.slug, query),
				staleTime: 60_000,
			};
		}),
	});
}

export function useUsageByApiKeyQuery(id: string, query: UsageQuery) {
	return useQuery({
		queryKey: queryKeys.usageByApiKey(id, query),
		queryFn: () => apiAdapter.usage.byApiKey(id, query),
		enabled: Boolean(id),
		placeholderData: keepPreviousData,
	});
}

export function useWorkspaceSettingsQuery() {
	return useQuery({
		queryKey: queryKeys.workspaceSettings,
		queryFn: () => apiAdapter.settings.workspace(),
	});
}

export function useWorkspaceMembersQuery() {
	return useQuery({
		queryKey: queryKeys.workspaceMembers,
		queryFn: () => apiAdapter.settings.members(),
	});
}

export function useWorkspaceInvitationsQuery() {
	return useQuery({
		queryKey: queryKeys.workspaceInvitations,
		queryFn: () => apiAdapter.settings.invitations(),
	});
}

export function useInviteMemberMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (input: InviteMemberInput) => apiAdapter.settings.invite(input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceInvitations });
		},
	});
}

export function useUpdateWorkspaceMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (input: UpdateWorkspaceInput) => apiAdapter.settings.updateWorkspace(input),
		onSuccess: (workspace) => {
			queryClient.setQueryData(queryKeys.workspaceSettings, workspace);
		},
	});
}

/** Shows the new role right away and puts the old one back if the server refuses. */
export function useUpdateMemberRoleMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ memberId, role }: { memberId: string; role: WorkspaceMember["role"] }) =>
			apiAdapter.settings.updateMemberRole(memberId, role),
		onMutate: async ({ memberId, role }) => {
			await queryClient.cancelQueries({ queryKey: queryKeys.workspaceMembers });
			const previous = queryClient.getQueryData<WorkspaceMember[]>(queryKeys.workspaceMembers);
			queryClient.setQueryData<WorkspaceMember[]>(queryKeys.workspaceMembers, (members) =>
				members?.map((member) => (member.id === memberId ? { ...member, role } : member)),
			);
			return { previous };
		},
		onError: (_error, _input, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKeys.workspaceMembers, context.previous);
			}
		},
		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceMembers });
		},
	});
}

export function useRemoveMemberMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (memberId: string) => apiAdapter.settings.removeMember(memberId),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceMembers });
		},
	});
}

export function useCancelInvitationMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (invitationId: string) => apiAdapter.settings.cancelInvitation(invitationId),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceInvitations });
		},
	});
}

export function useRequestPasswordResetMutation() {
	return useMutation({
		mutationFn: (email: string) => apiAdapter.auth.requestPasswordReset(email),
	});
}

export function useResetPasswordMutation() {
	return useMutation({
		mutationFn: ({ token, newPassword }: { token: string; newPassword: string }) =>
			apiAdapter.auth.resetPassword(token, newPassword),
	});
}

export function useVerifyEmailMutation() {
	return useMutation({
		mutationFn: (token: string) => apiAdapter.auth.verifyEmail(token),
	});
}

/* ─── Human approvals ───────────────────────────────────────────── */

export function useApprovalsQuery(filters: ApprovalFilters, options: { refetchMs?: number } = {}) {
	return useQuery({
		queryKey: queryKeys.approvals(filters),
		queryFn: () => apiAdapter.approvals.list(filters),
		refetchInterval: options.refetchMs ?? 15_000,
		staleTime: 0,
	});
}

export function useApprovalQuery(id: string) {
	return useQuery({
		queryKey: queryKeys.approval(id),
		queryFn: () => apiAdapter.approvals.get(id),
		enabled: Boolean(id),
	});
}

/** The nav badge: cheap, polled, and never blocks a page. */
export function usePendingApprovalCountQuery() {
	return useQuery({
		queryKey: queryKeys.approvalPendingCount,
		queryFn: () => apiAdapter.approvals.pendingCount(),
		refetchInterval: 30_000,
		staleTime: 10_000,
	});
}

export function useResolveApprovalMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, input }: { id: string; input: ApprovalDecisionInput }) =>
			apiAdapter.approvals.resolve(id, input),
		onSuccess: (resolved) => {
			void queryClient.invalidateQueries({ queryKey: ["approvals"] });
			if (resolved.conversationId) {
				void queryClient.invalidateQueries({
					queryKey: queryKeys.conversationDetail(resolved.conversationId),
				});
			}
		},
	});
}
